import CurrencyIcon from 'components/CurrencyIcon/CurrencyIcon';

import { formatCurrency, getCurrencyTypeLabel } from 'utils/currencies';

import { useNavigate } from '@tanstack/react-router';
import { CustomLoader } from 'components/CustomLoader/CustomLoader';
import {
  ProductsWithHoldingsAndRates,
  useFunctionalCurrency,
  useProductsWithHoldingsAndRates,
} from 'hooks/products/useProducts';
import useAppLanguage from 'hooks/useAppLanguage';
import { orderBy } from 'utils/sorting';
import {
  ListContainer,
  PortfolioListHeader,
  PortfolioListItem,
  Typography,
} from '@altify/components';
import clsx from 'clsx';
import { ActiveProductDtoTypeEnum } from 'services/generated';
import GainLoss from 'components/GainLoss/GainLoss';
import formatNumber from 'utils/format-number';
import { PortfolioActionButton } from './PortfolioActionButton';
import { useState } from 'react';
import filterIcon from 'assets/icons/filter-icon.svg';
import { portfolioFilteringOptions } from './portfolioFilterOptions';
import { PortfolioSortingModal } from './PortfolioSortingModal';
import { PortfolioTranslations } from '../messages';

export type SortingType = 'holdings' | 'gainLoss' | 'breakdownPercentage';
const PortfolioHoldingsTable = ({
  isMobile,
  messages,
}: {
  isMobile: boolean;
  messages: PortfolioTranslations;
}) => {
  const navigate = useNavigate();
  const language = useAppLanguage();
  const [isSortingModalOpen, setIsSortingModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] =
    useState<SortingType>('holdings');

  const { products, isLoading } = useProductsWithHoldingsAndRates();
  const { functionalCurrency } = useFunctionalCurrency();
  const productsWithHoldings = products?.filter(p => p.totalAmount > 0);

  const validProducts = productsWithHoldings?.filter(
    p =>
      p.type !== ActiveProductDtoTypeEnum.Locked &&
      p.type !== ActiveProductDtoTypeEnum.Fiat,
  );

  const sortedProducts = validProducts?.slice()?.sort(orderBy('', 'asc'));

  const lockedProducts = productsWithHoldings
    ?.filter(p => p.type === ActiveProductDtoTypeEnum.Locked)
    ?.sort(orderBy('', 'asc'));

  const sortedData = [...(sortedProducts ?? []), ...(lockedProducts ?? [])];

  const combinedData =
    sortedData.map(p => {
      return {
        code: p.code,
        title: p.name,
        subtitle: getCurrencyTypeLabel(p.code, p.type, language),
        isBuyable: p.isBuyable,
        isSellable: p.isSellable,
        iconComponent: () => (
          <CurrencyIcon
            currencyCode={p.code}
            isLockedAsset={p.type == ActiveProductDtoTypeEnum.Locked}
          />
        ),
        holdingsColumn: (
          <div className="flex flex-col items-end">
            <>
              <Typography size="medium" className="text-white">
                {formatCurrency(p.totalAmount, p.code)}
              </Typography>
              {p.type !== ActiveProductDtoTypeEnum.Locked ? (
                <Typography size="medium" className="text-white">
                  {formatCurrency(
                    p.totalAmountInDisplay,
                    functionalCurrency?.code,
                  )}
                </Typography>
              ) : null}
            </>
          </div>
        ),
        returnsColumn:
          p?.type !== ActiveProductDtoTypeEnum.Locked ? (
            <div className="flex flex-col items-end">
              {p?.type === ActiveProductDtoTypeEnum.PrivateCredit ? (
                <>
                  <GainLoss
                    value={11}
                    isPercentage
                    background={true}
                    hidePrefix={true}
                  />
                  <Typography size="medium" className="text-white">
                    {messages.holdings.annualYield}
                  </Typography>
                </>
              ) : (
                <>
                  <GainLoss
                    value={isLoading ? null : p.unrealizedGainLossPercentage}
                    isPercentage
                    background={true}
                  />
                  <GainLoss
                    value={isLoading ? null : p.unrealizedGainLossAmount}
                    symbol={functionalCurrency?.symbol!}
                    className={`${isLoading ? 'h-2 mb-2' : ''}`}
                  />
                </>
              )}
            </div>
          ) : null,
        weightingColumn: (
          <Typography size="medium" className="text-white">
            {formatNumber(p.breakdownPercentage, 2)}%
          </Typography>
        ),
        actionsColumn: isMobile ? (
          renderMobile(p, selectedCategory, functionalCurrency?.code!, messages)
        ) : (
          <PortfolioActionButton
            productCode={p.code}
            productType={p.type}
            isBuyable={p.isBuyable}
            isSellable={p.isSellable}
          />
        ),
      };
    }) ?? [];

  return (
    <>
      <div className={'px-2 pb-2'}>
        {combinedData.length > 0 && !isLoading && (
          <div className="flex flex-col md:border-2 md:border-dark-blue md:rounded-xxl md:mx-4 md:mt-4 md:px-2 md:py-4 mb-24">
            <ListContainer>
              {combinedData.length && (
                <PortfolioListHeader
                  isMobile={isMobile}
                  className="p-2"
                  assetHeader={'Asset'}
                  holdingsHeader={!isMobile ? 'Holdings' : ''}
                  holdingsComponent={
                    <button
                      onClick={() => setIsSortingModalOpen(true)}
                      className="flex flex-row items-center gap-4">
                      <Typography size="small" className="text-neutral-200">
                        {
                          portfolioFilteringOptions?.find(
                            item => item.value === selectedCategory,
                          )?.label?.[language]
                        }
                      </Typography>
                      <img
                        className="text-white rounded-lg p-1 w-10 h-10 bg-blue-grey"
                        src={filterIcon}
                      />
                    </button>
                  }
                  returnsHeader="Return"
                  weightsHeader="Weighting"
                />
              )}
              {combinedData.map((p, index) => (
                <PortfolioListItem
                  key={p.code}
                  {...p}
                  assetColumn={
                    <div className="flex-column justify-center">
                      <Typography variant="callToAction" className="text-white">
                        {p.title}
                      </Typography>
                      <Typography size="small" className="text-neutral-300">
                        {p.subtitle}
                      </Typography>
                    </div>
                  }
                  onPress={() => navigate({ to: `/products/${p.code}` })}
                  className={clsx(
                    'px-4',
                    index === 0 && 'rounded-tr-2xl rounded-tl-2xl',
                    index === combinedData.length - 1 &&
                      'rounded-br-2xl rounded-bl-2xl',
                  )}
                  isMobile={isMobile}
                  showChevron={!isMobile}
                />
              ))}
            </ListContainer>
          </div>
        )}
        {isLoading && (
          <div className="py-8 pb-6">
            <CustomLoader />
          </div>
        )}
      </div>
      {isSortingModalOpen && (
        <PortfolioSortingModal
          isOpen={true}
          onClose={() => setIsSortingModalOpen(false)}
          sorting={selectedCategory}
          setSorting={setSelectedCategory}
        />
      )}
    </>
  );
};

const renderMobile = (
  p: ProductsWithHoldingsAndRates[number],
  selectedCategory: SortingType,
  functionalCurrencyCode: string,
  messages: PortfolioTranslations,
) => {
  const isLocked = p?.type === ActiveProductDtoTypeEnum.Locked;

  switch (selectedCategory) {
    case 'breakdownPercentage':
      return isLocked ? null : (
        <div className="flex flex-col items-end">
          <Typography size="medium">
            {p?.breakdownPercentage?.toFixed(2)}%
          </Typography>
        </div>
      );
    case 'gainLoss':
      return p?.type == ActiveProductDtoTypeEnum.PrivateCredit ? (
        <div className="flex flex-col items-end">
          <div className="flex flex-col items-end">
            <GainLoss
              value={11}
              isPercentage
              background={true}
              hidePrefix={true}
            />
            <Typography size="medium" className="text-white">
              {messages.holdings.annualYield}
            </Typography>
          </div>
        </div>
      ) : !isLocked ? (
        <div className="flex flex-col items-end">
          <Typography size="large" weight="medium">
            {formatCurrency(p?.totalAmountInDisplay, functionalCurrencyCode)}
          </Typography>
          <GainLoss
            value={p?.unrealizedGainLossPercentage}
            isPercentage
            background={true}
          />
        </div>
      ) : null;

    case 'holdings':
    default:
      return (
        <div className="flex flex-col items-end">
          <Typography size="large" weight="medium">
            {formatCurrency(p?.totalAmount, p.code)}
          </Typography>
          {!isLocked && p.isTracked && (
            <Typography size="medium">
              {formatCurrency(p?.totalAmountInDisplay, functionalCurrencyCode)}
            </Typography>
          )}
        </div>
      );
  }
};

export default PortfolioHoldingsTable;
